import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import reviewApi from "../api/reviewApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, PageSkeleton } from "../components/common/PageState";
import ReviewProgressTracker from "../components/reviews/ReviewProgressTracker";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDate, recordId, unwrapRows } from "../utils/normalize";

const tabs = [
  { label: "Active", status: "active" },
  { label: "Completed", status: "completed" },
  { label: "Acknowledged", status: "acknowledged" },
];

const getWorkflowState = (review = {}) =>
  String(review.workflowState?.currentState || review.currentStage || review.finalStatus || "DRAFT").toUpperCase();

const getTrackerStage = (state) => {
  switch (String(state || "").toUpperCase()) {
    case "DRAFT":
    case "NOT_STARTED":
    case "SELF_ASSESSMENT":
    case "SELF_ASSESSMENT_IN_PROGRESS":
      return "SELF_ASSESSMENT";
    case "SELF_ASSESSMENT_SUBMITTED":
    case "MANAGER_REVIEW":
    case "MANAGER_ASSESSMENT":
    case "MANAGER_REVIEW_IN_PROGRESS":
      return "MANAGER_REVIEW";
    case "MANAGER_REVIEW_SUBMITTED":
    case "PENDING_CALIBRATION":
    case "CALIBRATION":
    case "CALIBRATED":
      return "CALIBRATION";
    case "PENDING_ACKNOWLEDGEMENT":
      return "ACKNOWLEDGEMENT";
    case "ACKNOWLEDGED":
    case "FINALIZED":
    case "COMPLETED":
    case "CLOSED":
      return "COMPLETED";
    default:
      return state;
  }
};

const getPrimaryAction = (review) => {
  const actions = review.allowedActions || [];
  const status = String(review.status || review.finalStatus || "").toUpperCase();
  if (actions.includes("ACKNOWLEDGE_REVIEW")) return "Acknowledge";
  if (actions.includes("SUBMIT_MANAGER_REVIEW")) return "Continue manager review";
  if (actions.includes("SUBMIT_SELF_ASSESSMENT")) return "Continue self-assessment";
  if (actions.includes("SAVE_MANAGER_REVIEW")) return "Start manager review";
  if (actions.includes("SAVE_SELF_ASSESSMENT")) return "Start self-assessment";
  if (status === "DRAFT") return "Start self-assessment";
  if (status === "ACKNOWLEDGED" || status === "COMPLETED") return "Download report";
  return "View review";
};

const ReviewRecord = ({ review }) => {
  const navigate = useNavigate();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const reviewId = recordId(review);
  const trackerStage = getTrackerStage(getWorkflowState(review));

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
      <Flex justify="space-between" align={{ base: "stretch", md: "flex-start" }} direction={{ base: "column", md: "row" }} gap={3}>
        <Box>
          <HStack wrap="wrap">
            <Text fontWeight="semibold">{review.name || review.title || review.reviewName || "Performance review"}</Text>
            <StatusBadge value={review.status || review.finalStatus || "IN_PROGRESS"} />
          </HStack>
          <Text mt={1} fontSize="sm" color={mutedText}>
            Review period: {formatDate(review.periodStart || review.startDate)} - {formatDate(review.periodEnd || review.endDate)}
          </Text>
          <Text fontSize="sm" color={mutedText}>
            Due date: {formatDate(review.dueDate)}
          </Text>
        </Box>
        <Button size="sm" colorScheme="blue" onClick={() => navigate(`/performance/reviews/${reviewId}`)} alignSelf={{ base: "stretch", md: "flex-start" }}>
          {getPrimaryAction(review)}
        </Button>
      </Flex>
      <Box mt={4}>
        <ReviewProgressTracker stages={review.workflowStages || review.workflow?.stages} currentStage={trackerStage} />
      </Box>
    </Box>
  );
};

const MyReviewsPage = () => {
  const [tabIndex, setTabIndex] = React.useState(0);
  const params = { scope: "my", statusGroup: tabs[tabIndex].status };
  const reviewsQuery = useQuery({
    queryKey: performanceQueryKeys.reviews.my(params),
    queryFn: () => reviewApi.listReviews(params),
  });
  const rows = unwrapRows(reviewsQuery.data);

  return (
    <ModulePageShell title="My reviews" description="View and complete your performance reviews." onRefresh={() => reviewsQuery.refetch()}>
      <OperationalPanel>
        <Tabs size="sm" colorScheme="blue" index={tabIndex} onChange={setTabIndex}>
          <TabList>
            {tabs.map((tab) => <Tab key={tab.status}>{tab.label}</Tab>)}
          </TabList>
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel key={tab.status} px={0}>
                {reviewsQuery.isLoading ? (
                  <PageSkeleton rows={4} />
                ) : rows.length === 0 ? (
                  <CompactEmptyState title={`No ${tab.label.toLowerCase()} reviews.`} />
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {rows.map((review) => <ReviewRecord key={recordId(review)} review={review} />)}
                  </VStack>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </OperationalPanel>
    </ModulePageShell>
  );
};

export default MyReviewsPage;
